import { permissionAllowed, Salutation, User, UserDetails } from "@ews/react-data";
import { useL10n } from "@ews/react-localization-context";
import { IonButton, IonItem } from "@ionic/react";
import { useModifyUser, useModifyUserDetails, useUser, useUserDetails } from "../../../ReactData/user";
import SettingsCol from "../../../Settings/SettingsCol/SettingsCol";
import SettingsCheckbox from "../../../Settings/SettingsComponents/SettingsCeckbox/SettingsCeckbox";
import SettingsLabel from "../../../Settings/SettingsComponents/SettingsLabel/SettingsLabel";
import SettingsSelect from "../../../Settings/SettingsComponents/SettingsSelect/SettingsSelect";
import SettingsSelectOption from "../../../Settings/SettingsComponents/SettingsSelectOption/SettingsSelectOption";
import SettingsTextInput from "../../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import { MainSettingsProps } from "./types";

import CustomerTypeAhead from "../../../Customer/CustomerTypeAhead";
import { useAlert, useDialog } from "../../../Dialog";
import Feature from "../../../Feature";
import { useForgotPasswordRpc, useMyPermissions, useMyProfileId } from "../../../Login/LoginProvider";
import style from "./../SettingsOvererlay/SettingsOverlay.module.scss";
import { LogoutButton } from "./LogoutButton/LogoutButton";


export const getSalutationTranslation = (salutation: string) =>
{
    return `[REACT_SALUTATION.${salutation.toUpperCase()}]`;
};


const MainSettings: React.FC<MainSettingsProps> = ({ id }) =>
{
    const { translate: t } = useL10n();
    const dialog = useDialog();
    const alert = useAlert();

    const user = useUser(id || "");
    const userDetails = useUserDetails(id || "");
    const myPermissions = useMyPermissions();

    const myId = useMyProfileId();
    const allowedToEdit = myId === id ? true : permissionAllowed(myPermissions.createUser || 0);

    const modifyUser = useModifyUser();
    const modifyUserDetails = useModifyUserDetails();
    const forgotPassword = useForgotPasswordRpc();


    // const customer = useCustomer(user.customerId || "");
    const updateUserDetails = async (data: UserDetails) =>
    {
        await modifyUserDetails(user.id!, data);
    };

    const updateUser = async (data: User) =>
    {
        await modifyUser(user.id!, data);
    };


    return <>
        <SettingsCol>
            <SettingsCheckbox
                disabled={!allowedToEdit}
                text={"COM_BANNERS_HEADING_ACTIVE"}
                checked={user.active || false}
                onChange={(active) => { updateUser({ active } as User); }}
            />

            <SettingsLabel
                text={"COM_FACPMANAGEMENT_USERS_USERID"}
                value={user?.username!}
            />

            <SettingsSelect
                disabled={!allowedToEdit}
                text={"COM_FACPMANAGEMENT_USERS_SALUTATION"}
                value={userDetails.salutation}
                onSelect={(salutation) => updateUserDetails({ salutation } as UserDetails)}
            >
                {Object.keys(Salutation).map((salutation) =>
                {
                    return <SettingsSelectOption key={salutation} text={getSalutationTranslation(salutation)} value={salutation} />;
                })}
            </SettingsSelect>

            <SettingsTextInput
                disabled={!allowedToEdit}
                text={"PLG_USER_PROFILE_LST_FIELD_ACADEMIC_TITLE_LABEL"}
                inputType={"text"}
                value={userDetails?.title}
                onChange={(title) => { updateUserDetails({ title } as UserDetails); }} />


            <SettingsTextInput
                disabled={!allowedToEdit}
                required={true}
                text={"COM_FACPMANAGEMENT_USERS_FIRSTNAME"}
                inputType={"text"}
                value={userDetails?.firstName}
                onChange={(firstName) => { updateUserDetails({ firstName } as UserDetails); }} />

            <SettingsTextInput
                disabled={!allowedToEdit}
                required={true}
                text={"COM_FACPMANAGEMENT_USERS_LASTNAME"}
                inputType={"text"}
                value={userDetails?.lastName}
                onChange={(lastName) => { updateUserDetails({ lastName } as UserDetails); }} />

            <SettingsTextInput
                disabled={!allowedToEdit}
                text={"PLG_USER_PROFILE_LST_FIELD_OCCUPATION_LABEL"}
                inputType={"text"}
                value={userDetails.occupation}
                onChange={(occupation) => { updateUserDetails({ occupation } as UserDetails); }} />

            <SettingsTextInput
                disabled={!allowedToEdit}
                required={true}
                text={"COM_CONTACT_EMAIL_LABEL"}
                inputType={"text"}
                value={user?.email}
                onChange={(email) => { updateUser({ email } as User); }} />


            <SettingsTextInput
                disabled={!allowedToEdit}

                text={"PLG_USER_PROFILE_LST_FIELD_PHONE_LABEL"}
                inputType={"text"}
                value={userDetails.landLine}
                onChange={(landLine) => { updateUserDetails({ landLine } as UserDetails); }}
            />

            <SettingsTextInput
                disabled={!allowedToEdit}
                text={"COM_FACPMANAGEMENT_USERS_MOBILE"}
                inputType={"text"}
                value={userDetails.phoneNumber}
                onChange={(phoneNumber) => { updateUserDetails({ phoneNumber } as UserDetails); }}
            />

            <CustomerTypeAhead
                id="user-main-setting"
                currentId={user.customerId || ""}
                label={"COM_FACPMANAGEMENT_CUSTOMER_FIELD_PARENT_LABEL"}
                disabled={!permissionAllowed(myPermissions.isAdmin!)}
                onChange={(customerId => updateUser({ customerId } as User))}
            />

        </SettingsCol>
        <SettingsCol>
            <IonItem>
                <IonButton
                    disabled={!allowedToEdit}
                    expand="full"
                    className={style.btn}
                    onClick={async () =>
                    {
                        const confirm = await dialog({
                            title: t('COM_FACPMANAGEMENT_USER_FIELD_PASSWORD_RESET_LABEL'),
                            message: t('COM_FACPMANAGEMENT_RESET_COMPLETE_MAIL_SENT'),
                            confirmButtonTitle: t('[SID_YES]'),
                            cancelButtonTitle: t('COM_BANNERS_CANCEL')
                        });
                        if (confirm) {
                            try {
                                await forgotPassword(user.username!); //updateUser({ 'accessToken': null } as User);
                            } catch (e: any) {
                                const { translationKey } = e;
                                alert({
                                    "title": t(translationKey),
                                    message: t(translationKey),
                                    "buttonTitle": t('Dismiss')
                                });
                            }
                        }
                    }
                    }
                >
                    {t('COM_FACPMANAGEMENT_USER_FIELD_PASSWORD_RESET_LABEL')}
                </IonButton>
            </IonItem>
            <LogoutButton id={id || ""} className={style.btn} />

            <Feature feature="SETTINGS_TEMPLATE">
                {permissionAllowed(myPermissions.isAdmin!) && <IonItem>
                    <IonButton
                        disabled={!allowedToEdit}
                        className={style.btn}
                        expand="full"
                        onClick={() => { }}
                    >
                        {t("COM_FACPMANAGEMENT_USER_FIELD_ISTEMPLATE_LABEL")}
                    </IonButton>
                </IonItem>}
            </Feature>
        </SettingsCol>
        <SettingsCol>

        </SettingsCol>

    </>;
};

export default MainSettings;
